import { localStamp, paperStamp } from './paperDate';
import {
  ALIGN_CENTER,
  ALIGN_LEFT,
  BOLD_OFF,
  BOLD_ON,
  CUT,
  FEED_BEFORE_CUT,
  INIT,
  SIZE_DOUBLE_BOLD,
  SIZE_NORMAL,
  WIDTH,
  feed,
  line,
  rule,
  wrap,
} from './escpos';

/**
 * The kitchen ticket.
 *
 * A port of the phone's `api/station_ticket.dart`, line for line, so a cook
 * cannot tell whether the order was rung in on a phone or on the till.
 *
 * A station ticket is not a receipt. No prices, no totals, no logo — a cook
 * needs none of it and it costs paper on every order. What it does carry is a
 * large order and table number, quantity-first lines, and the note, which is
 * where "no onions" lives.
 */

/**
 * Some printers beep on `ESC B`; others print the bytes as characters. Off
 * until it has been tried on real hardware here, exactly as on the phone.
 */
const BUZZER_ENABLED = false;
const BUZZER = '\x1B\x42\x02\x02';

/**
 * @param {object} order   orderNumber, tableNumber, orderType, note, date
 * @param {object} entry   one print-plan entry: { printer, items }
 * @param {string} waiterName  who rang it in — the name the kitchen asks for
 */
export function buildStationTicket({ order, entry, waiterName }) {
  let out = INIT;
  if (BUZZER_ENABLED) out += BUZZER;

  /* ── which station ── */

  out += ALIGN_CENTER;
  out += SIZE_DOUBLE_BOLD;
  out += line(String(entry.printer.name || '').toUpperCase());
  out += SIZE_NORMAL;
  out += feed(1);

  /* ── which order, and whose ── */

  out += line(`ORDER #${order.orderNumber}`);

  // Who took the order goes first and bold: when the kitchen has a question
  // about a ticket, this is the name they need.
  const staff = String(waiterName || order.waiterName || '').trim();
  if (staff) {
    out += BOLD_ON;
    out += line(staff);
    out += BOLD_OFF;
  }

  out += feed(1);

  const table = String(order.tableNumber || '').trim();
  if (table) {
    out += SIZE_DOUBLE_BOLD;
    out += line(`TABLE ${table}`);
    out += SIZE_NORMAL;
  }

  // Takeaway and delivery change how the food is plated and packed, so the
  // kitchen has to see it. Dine-in is the default and says nothing.
  const type = String(order.orderType || '').trim();
  if (type && type !== 'dine-in') {
    out += SIZE_DOUBLE_BOLD;
    out += line(type.toUpperCase());
    out += SIZE_NORMAL;
  }

  /**
   * An amended order.
   *
   * The lines below are not the order — they are what was ADDED to an order
   * the kitchen already has. The server works that out by diffing the saved
   * items against the new ones, so nothing already cooking is on this paper.
   *
   * Which is why the heading says so in the words the kitchen uses: Dheeraad,
   * an addition, followed by a blank line so the new lines read as their own
   * short list rather than as the whole ticket starting again.
   */
  if (order.isUpdate) {
    out += SIZE_DOUBLE_BOLD;
    out += line('Dheeraad');
    out += SIZE_NORMAL;
    out += line('waxaa lagu soo daray dalab hore');
    out += feed(1);
  } else if (order.isReprint) {
    out += SIZE_DOUBLE_BOLD;
    out += line('** REPRINT **');
    out += SIZE_NORMAL;
  }

  out += ALIGN_LEFT;
  out += rule('-');

  /* ── the food ── */

  (entry.items || []).forEach((item) => {
    const prefix = `${item.quantity}  `;
    const available = WIDTH - prefix.length;
    const lines = wrap(item.name, available);

    out += BOLD_ON;
    out += line(prefix + lines[0]);
    lines.slice(1).forEach((continuation) => {
      out += line(' '.repeat(prefix.length) + continuation);
    });
    out += BOLD_OFF;
  });

  out += rule('-');

  /* ── the note, in the biggest type on the paper ── */

  const note = String(order.note || '').trim();
  if (note) {
    out += BOLD_ON;
    out += line('NOTE');
    out += BOLD_OFF;
    wrap(note, WIDTH - 2).forEach((noteLine) => {
      out += SIZE_DOUBLE_BOLD;
      out += line(noteLine);
      out += SIZE_NORMAL;
    });
    out += rule('-');
  }

  /* ── when ── */

  out += line(paperStamp(order.date));

  // Enough paper to clear the cutter — three lines cut through this line.
  out += feed(FEED_BEFORE_CUT);
  out += CUT;

  return out;
}

/**
 * The slip a manager prints to confirm which physical machine an IP belongs
 * to. It has to be readable from across the room — it is the only way to
 * catch two stations wired the wrong way round.
 */
export function buildTestSlip(printer) {
  let out = INIT;
  out += ALIGN_CENTER;
  out += feed(1);

  out += SIZE_DOUBLE_BOLD;
  out += line(String(printer.name || '').toUpperCase());
  out += SIZE_NORMAL;

  out += rule('-');
  out += line('Test print');
  out += line(`${printer.host}:${printer.port || 9100}`);
  out += line(localStamp());

  out += feed(FEED_BEFORE_CUT);
  out += CUT;

  return out;
}
